import React, { useState } from "react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Lock, Check, Loader2, Sparkles, Compass, Target, FileCheck2, FlaskConical, Layers } from "lucide-react";

const STEPS = [
  { icon: Layers, label: "Your profile", note: "Constraints, time you can spare, and what you will not do" },
  { icon: Sparkles, label: "Your capabilities", note: "Pulled from your own resume, each shown with its source line" },
  { icon: Compass, label: "Income routes", note: "Service, training, product, advisory and more — categories, not advice" },
  { icon: Target, label: "Your direction", note: "Up to 5 scored hypotheses; you choose the one to pursue" },
  { icon: FileCheck2, label: "Your blueprint", note: "Offer, customer, price hypothesis and your first validation action" },
  { icon: FlaskConical, label: "Validate", note: "A structured target to test against, with a shareable PDF" },
];

const INCLUDED = [
  "Capability extraction from your resume with the exact source line",
  "Up to 5 income hypotheses scored on 10 fit dimensions",
  "A blueprint with offer, buyer, price hypothesis and first test",
  "Branded blueprint PDF you can download and share",
  "Your journey to an additional ₹10K, tracked step by step",
];

const AcceleratorPaywall = ({ membership, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const requested = membership?.upgrade_requested;

  const request = async () => {
    setBusy(true);
    try {
      await api.post("/membership/upgrade-request");
      toast.success("Request sent — we'll open Growth for you shortly");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="accelerator-paywall">
      <div className="accent-bar card-soft p-5 sm:p-6" style={{ "--accent": "#0D9488" }}>
        <p className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-teal-700">
          <Lock className="w-3.5 h-3.5" /> Growth membership
        </p>
        <h2 className="mt-3 text-xl sm:text-2xl font-bold tracking-tight text-slate-900">A second income, built from the career you already have</h2>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">The 2nd Income Accelerator turns your own experience into testable income hypotheses — alongside your primary job, and validated before you commit. It's part of Growth.</p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={request} disabled={busy || requested} className="h-12 bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-60" data-testid="paywall-upgrade-button">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {requested ? "Growth access requested" : "Unlock with Growth"}
          </Button>
          <p className="text-xs text-slate-500">Your resume tools stay exactly as they are — nothing here affects your current plan.</p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="paywall-steps">
        {STEPS.map((s, i) => (
          <div key={s.label} className="card-soft p-4 relative overflow-hidden" data-testid={`paywall-step-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="inline-flex items-center gap-2 text-sm font-bold text-slate-900"><s.icon className="w-4 h-4 text-teal-600" /> {s.label}</p>
              <Lock className="w-3.5 h-3.5 shrink-0 text-slate-300" />
            </div>
            <p className="mt-2 text-xs text-slate-600 leading-relaxed">{s.note}</p>
            <span className="mt-3 block h-1.5 w-full rounded-full bg-slate-100"><span className="block h-1.5 rounded-full bg-teal-200" style={{ width: `${18 + i * 14}%` }} /></span>
          </div>
        ))}
      </div>

      <div className="card-soft p-5" data-testid="paywall-included">
        <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">What Growth unlocks here</p>
        <ul className="mt-3 space-y-2">
          {INCLUDED.map((x) => (
            <li key={x} className="flex gap-2.5 text-sm text-slate-700"><Check className="w-4 h-4 shrink-0 text-emerald-600 mt-0.5" /><span>{x}</span></li>
          ))}
        </ul>
        <p className="mt-4 text-xs text-slate-500">Hypotheses are planning indicators built from your profile — never predictions of income.</p>
      </div>
    </div>
  );
};

export default AcceleratorPaywall;
